import type { SwooshColor } from "@ui/components/Swoosh";
import { type ReactNode, useState } from "react";
import { AboutAccountModal } from "@/components/AboutAccountModal/AboutAccountModal";
import { type CoverCity, CoverFrame, type CoverKind } from "@/components/CoverFrame/CoverFrame";
import { Identity, type IdentityAccount } from "@/components/Identity/Identity";
import { type ProfileTabHrefs, type ProfileTabKey, ProfileTabs } from "@/components/ProfileTabs/ProfileTabs";
import { RestrictedProfile } from "@/components/RestrictedProfile/RestrictedProfile";

export type ProfileType = "Individual" | "Family" | "Community";

/** Swoosh colour for each profile type's default cover; mirrors AccountAvatar's ACCOUNT_TYPE_COLORS. */
export const PROFILE_SWOOSH_COLORS: Record<ProfileType, SwooshColor> = {
  Community: "yellow",
  Family: "red",
  Individual: "purple",
};

/** Normalized account shape shared by the individual, family and community profile chrome. */
export interface ProfileAccount extends IdentityAccount {
  profile_type: ProfileType;
  account_created_at: string;
  cover_kind: CoverKind;
  cover_city: CoverCity | null;
}

export interface ProfileFrameProps {
  account: ProfileAccount;
  currentTab: ProfileTabKey;
  /** Tab hrefs when the profile has a store; omit to render without tab nav. */
  tabs?: ProfileTabHrefs;
  restricted?: boolean;
  onAcceptFriendship?: () => void;
  onDeclineFriendship?: () => void;
  onFriendshipAction?: () => void;
  onShareProfile?: () => void;
  onUnfriend?: () => void;
  onViewFriends?: () => void;
  children: ReactNode;
}

/**
 * Chrome shared by the individual, family and community profile and store
 * pages (`/@handle` and `/@handle/store`): cover, identity header, about
 * modal, and the tab nav. Restricted profiles render the restricted notice
 * instead of tab content. Area-specific sections (e.g. community programs)
 * are injected as children.
 */
export function ProfileFrame({
  account,
  currentTab,
  tabs,
  restricted,
  onAcceptFriendship,
  onDeclineFriendship,
  onFriendshipAction,
  onShareProfile,
  onUnfriend,
  onViewFriends,
  children,
}: ProfileFrameProps) {
  const [aboutOpen, setAboutOpen] = useState(false);

  return (
    <>
      <CoverFrame
        coverCity={account.cover_city}
        coverImage={account.cover_image}
        coverKind={account.cover_kind}
        swooshColor={PROFILE_SWOOSH_COLORS[account.profile_type]}
      />
      <div className="mx-auto flex w-full max-w-[820px] flex-col items-start pb-16 md:items-center md:text-center">
        {/* The CTA renders on a restricted profile, but messaging there needs a friend request
            first — so it stays a link to the messages page, which carries that policy. */}
        <Identity
          account={account}
          canDock={!restricted}
          onAboutAccount={() => setAboutOpen(true)}
          onAcceptFriendship={onAcceptFriendship}
          onDeclineFriendship={onDeclineFriendship}
          onFriendshipAction={onFriendshipAction}
          onShareProfile={onShareProfile}
          onUnfriend={onUnfriend}
          // `restricted` mirrors Social::Policies::CounterpartAccountPolicy#view_profile?, which makes the
          // friends endpoint 404 for these viewers — withholding the handler drops the action everywhere.
          onViewFriends={restricted ? undefined : onViewFriends}
        />
        <AboutAccountModal account={account} isOpen={aboutOpen} onOpenChange={setAboutOpen} />
        {tabs && (
          <div className="mt-8 flex w-full justify-center">
            <ProfileTabs currentTab={currentTab} profileHref={tabs.profileHref} storeHref={tabs.storeHref} />
          </div>
        )}
        {restricted ? (
          <RestrictedProfile className="mt-8 w-full lg:mt-12" displayName={account.display_name} />
        ) : (
          children
        )}
      </div>
    </>
  );
}
